/* This component uses cockpit layout as base so if cockpit layout changes this component should change too! */
import {Flex, Button, Text, Box, useTheme, useBreakpointValue} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React, {useEffect, useState, useRef, useCallback} from 'react'
import {FormattedMessage, defineMessages} from 'react-intl'
import {useHeader} from '../../../hooks/use-header'
import DisplayPrice from '../../../components/display-price'
import AddToCartCTA, {Skeleton as AddToCartCTASkeleton} from '../../../components/add-to-cart-cta'
import useMultiSite from '../../../hooks/use-multi-site'

const messages = defineMessages({
    overview: {defaultMessage: 'Overview', id: 'product_detail.sections.overview'},
    details: {defaultMessage: 'Details', id: 'product_detail.sections.details'},
    specifications: {defaultMessage: 'Specifications', id: 'pdp.module.specifications'},
    accessories: {
        defaultMessage: 'Accessories',
        id: 'product_detail.recommended_products.title.accessories'
    },
    moreInfos: {defaultMessage: 'More Infos', id: 'product_detail.sections.moreInfos'},
    reviews: {defaultMessage: 'Reviews', id: 'product_detail.sections.reviews'},
    howToUse: {defaultMessage: 'How To Use', id: 'product_detail.sections.howToUse'},
    spareParts: {defaultMessage: 'Spare Parts', id: 'product_detail.sections.spareParts'}
})

const PdpSubnavigation = ({
    isPromotionsLoading,
    isVariantLoading,
    product,
    promotions,
    sections
}) => {
    const {site} = useMultiSite()
    const {header, headerWrapper} = useHeader()
    const headerWrapperHeight = headerWrapper.clientHeight
    const subnavigationHeight = headerWrapper.clientHeight - header.clientHeight
    const theme = useTheme()
    const scrollTop = useRef(0)
    const [active, setActive] = useState(null)
    const [hiddenHeader, setHiddenHeader] = useState(false)
    const [show, setShow] = useState(false)
    const mobile = useBreakpointValue({base: true, lg: false})
    /* Anchor offset according dynamic header height */
    const anchorOffset = useRef()
    anchorOffset.current = hiddenHeader ? subnavigationHeight : headerWrapperHeight

    // we need use a ref here because we load some components async and
    // this allows the handleScroll function to always have the current filtered sections
    const filteredSections = useRef([])
    filteredSections.current = Object.fromEntries(
        Object.entries(sections).filter((arr) => arr?.[1]?.current)
    )

    /* Subnavigation links onclick - scroll to selected anchor of PDP */
    const scrollToAnchor = (id) => {
        const element = filteredSections.current[id].current

        if (element) {
            const scrollingDownwards = scrollTop.current < element.offsetTop
            anchorOffset.current = scrollingDownwards ? subnavigationHeight : headerWrapperHeight

            window.scrollTo({
                top: element.offsetTop - anchorOffset.current,
                behavior: 'smooth'
            })
        }
    }

    const handleScroll = useCallback(() => {
        requestAnimationFrame(() => {
            setHiddenHeader(scrollTop.current <= scrollY)
            setShow(scrollY > headerWrapperHeight * 2)

            scrollTop.current = scrollY

            /* Check if we reached a section and update active state of subnavigation */
            Object.entries(filteredSections.current).map(([key, {current}]) => {
                if (current) {
                    const start = current.offsetTop - anchorOffset.current
                    const end = current.offsetTop + current.clientHeight - anchorOffset.current

                    if (scrollY >= start && scrollY <= end) {
                        setActive(key)
                    }
                }
            })
        })
    }, [headerWrapperHeight])

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, {
            passive: true
        })

        /* Cleanup event listener */
        return () => window.removeEventListener('scroll', handleScroll)
    }, [handleScroll])

    const sidebarSizeProps = {
        // figma is based on 12 column grid
        // this is the rough equivalent in chakra
        flexBasis: '28.5%',
        // prevents children from stretching above the flex-basis size
        minWidth: 0
    }

    return (
        <Box bg="stokkeGray.bright" boxShadow={theme.shadows.bottom}>
            <Box
                padding={{
                    base: 4,
                    lg: '0.75rem 1rem 0.75rem 1.5rem'
                }}
                background="stokkeGray.bright"
                overflowX={{
                    base: 'auto',
                    lg: 'hidden'
                }}
                transition=".5s"
                sx={{
                    '&>*': {
                        flexShrink: 0
                    }
                }}
                scrollBehavior="smooth"
                display={show ? 'auto' : 'none'}
                maxWidth="container.xxl"
                mx="auto"
            >
                <Flex>
                    <Flex flex={1} gap={4} alignItems="center">
                        {Object.keys(filteredSections.current).map((section) => (
                            <Button
                                as={Text}
                                key={section}
                                size="sm"
                                variant="link"
                                textDecoration={active === section ? 'underline' : 'none'}
                                cursor="pointer"
                                _hover={mobile ? {} : null}
                                onClick={() => {
                                    scrollToAnchor(section)
                                }}
                                role="button"
                                tabIndex="1"
                            >
                                <FormattedMessage {...messages[section]} />
                            </Button>
                        ))}
                    </Flex>

                    <Box {...sidebarSizeProps} display={{base: 'none', lg: 'block'}}>
                        <Flex gap={2} align="center">
                            {!site?.isContentOnlySite && (
                                <Box>
                                    <DisplayPrice
                                        justifyContent="flex-start"
                                        loading={isPromotionsLoading || isVariantLoading}
                                        product={product}
                                        size="xl"
                                    />
                                    <Text textStyle="Regular Small" whiteSpace="nowrap">
                                        <FormattedMessage
                                            id="global.taxes_included"
                                            defaultMessage="tax included"
                                        />
                                    </Text>
                                </Box>
                            )}
                            {!isVariantLoading ? (
                                <AddToCartCTA
                                    isPromotionsLoading={isPromotionsLoading}
                                    isVariantLoading={isVariantLoading}
                                    product={product}
                                    promotions={promotions}
                                />
                            ) : (
                                <AddToCartCTASkeleton />
                            )}
                        </Flex>
                    </Box>
                </Flex>
            </Box>
        </Box>
    )
}

PdpSubnavigation.propTypes = {
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    product: PropTypes.object,
    promotions: PropTypes.array,
    sections: PropTypes.object
}

export default PdpSubnavigation
